import { atom, computed } from 'nanostores';
import { BOLT_IMPORT_ID, BOLT_SUPABASE_CONNECT_MSG_PREFIX, MSG_DEPLOY } from './constants';
import { isDeploymentMessage } from '~/lib/deploy';
import type { SetMessages } from '~/lib/hooks/useMessages';
import type { Message } from '~/types/chat';
import { hmrPropertyFactory } from '~/utils/hmr';
import { DOMAIN_MESSAGE_MARKER } from '~/utils/markers';
import { NOOP } from '~/utils/noops';

const hmr = hmrPropertyFactory('ChatHooks');

export interface UseChatHooks {
  sendMessage: (
    message?: string,
    options?: {
      details?: Record<string, unknown>;
    },
  ) => Promise<boolean>;
  setMessages: SetMessages;
  reload: () => Promise<string | null | undefined>;
  checkUserAcknowledgeLargeContext: (value: Message) => Promise<boolean>;
}

class ChatHooks {
  @hmr()
  hasMorePreviousMessages = atom(false);

  messages = atom<Message[]>([]);

  userMessageCountInHistory = computed(
    [this.messages, this.hasMorePreviousMessages],
    (messages, hasMorePreviousMessages) => {
      if (hasMorePreviousMessages) {
        return undefined;
      }

      return messages.filter(({ role }) => role === 'user').length;
    },
  );

  firstMessageInHistory = computed(
    [this.messages, this.hasMorePreviousMessages],
    (messages, hasMorePreviousMessages) => {
      if (hasMorePreviousMessages) {
        return undefined;
      }

      return messages.at(0);
    },
  );

  lastAssistantMessage = computed(this.messages, (messages) => {
    return messages.findLast(({ role, type, hidden }) => {
      return role === 'assistant' && type === 'chat' && !hidden;
    });
  });

  lastUserMessage = computed(this.messages, (messages) => {
    return messages.findLast(({ role, type }) => {
      return role === 'user' && type === 'chat';
    });
  });

  isDeploying = computed(this.messages, (messages) => {
    const lastMessage = messages.at(-1);

    if (!lastMessage) {
      return false;
    }

    if (isDeploymentMessage(lastMessage) && lastMessage.status === 'streaming') {
      return true;
    }

    if (lastMessage.type === 'chat' && lastMessage.role === 'user' && lastMessage.input === MSG_DEPLOY) {
      return true;
    }

    return false;
  });

  undoMessageInfo = computed(this.messages, (messages) => {
    const assistantMessages = messages.filter(
      ({ id, role, type, hidden }) =>
        !hidden &&
        role === 'assistant' &&
        type === 'chat' &&
        (id === BOLT_IMPORT_ID || id.startsWith(BOLT_SUPABASE_CONNECT_MSG_PREFIX) || !id.startsWith('bolt-')),
    );

    // message that should show "Undo"
    const from = assistantMessages.at(-1);

    // message where Undo-button restores to
    const to = assistantMessages.at(-2);

    if (!to || !from || from === to) {
      return false;
    }

    return { from, to };
  });

  lastDomainSetupMessage = computed(this.messages, (messages) => {
    return messages.findLast(({ role, type, content }) => {
      return role === 'assistant' && type === 'chat' && content.includes(DOMAIN_MESSAGE_MARKER);
    });
  });

  #hooks: UseChatHooks = {
    sendMessage: NOOP,
    setMessages: NOOP,
    reload: NOOP,
    checkUserAcknowledgeLargeContext: NOOP,
  };

  setHooks(hooks: Partial<UseChatHooks>) {
    Object.assign(this.#hooks, hooks);
  }

  get sendMessage() {
    return this.#hooks?.sendMessage;
  }

  get setMessages() {
    return this.#hooks?.setMessages;
  }

  get reload() {
    return this.#hooks?.reload;
  }

  get checkUserAcknowledgeLargeContext() {
    return this.#hooks?.checkUserAcknowledgeLargeContext;
  }
}

export const chatHooks = new ChatHooks();
